import { css } from '@emotion/react';
import { Trans } from 'i18n';
import { AccrualPointsNS } from 'models';
import { ReactNode } from 'react';
import Skeleton from 'react-loading-skeleton';
import type { ShortRule, Cabin, FareRules } from 'shared';

import { theme } from '../theme';

import { ShortRuleItem } from './ShortRuleItem';
import { getOrdinaryShortRules, getRefundAndRebookShortRules } from './utils';

const styles = {
  shortRules: (rows: number) => css`
    padding: 0;
    display: grid;
    grid-template-rows: repeat(${rows}, auto);
    grid-auto-flow: column;
    row-gap: ${theme.spacing.sm};
    column-gap: ${theme.spacing.xl};
    margin: 0;
    @media (max-width: ${theme.breakpoints.laptop}) {
      column-gap: 0;
    }
  `,
  fareRulesListWrapper: css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: calc(100% - 52px);
    padding: ${theme.spacing.md} ${theme.spacing.md} 0;
    font-size: ${theme.fontSize.sm};
  `,
  refundAndCancellationContainer: css`
    border-top: ${theme.borders.widths.sm} solid #80c5e5;
    padding: ${theme.spacing.sm} 0;
    margin-top: ${theme.spacing.md};
    border-bottom: ${theme.borders.widths.sm} solid var(--color-gray-2);
  `,
  points: css`
    display: flex;
    justify-content: flex-end;
    font: ${theme.typography.pSmall};
    margin-top: ${theme.spacing.sm};
    height: ${theme.spacing.md};
  `,
};

type ShortRulesProps = {
  rules: ShortRule[];
  maxRowCount?: number;
};

export function ShortRules({ rules, maxRowCount }: ShortRulesProps) {
  return (
    <ul css={styles.shortRules(maxRowCount ?? rules.length)}>
      {rules.map(rule => (
        <ShortRuleItem key={rule.id} rule={rule} />
      ))}
    </ul>
  );
}

type AccrualPointsData = { isLoading: boolean; data: AccrualPointsNS.TotalReward[] | undefined };

type Props = {
  cabin: Cabin | Cabin;
  accrualPoints?: AccrualPointsData;
  fareRules: Record<string, FareRules>;
  children?: ReactNode;
};

export function ShortRulesContainer(props: Props) {
  const { cabin, accrualPoints, fareRules, children } = props;

  const shortRules = fareRules[cabin.productCode]?.shortRules || [];
  const ordinaryShortRules = getOrdinaryShortRules(shortRules);
  const refundAndRebookShortRules = getRefundAndRebookShortRules(shortRules);

  return (
    <div css={styles.fareRulesListWrapper}>
      <div>
        {children && children}
        {ordinaryShortRules.length > 0 && <ShortRules rules={ordinaryShortRules} />}
      </div>

      <div css={styles.refundAndCancellationContainer}>
        {refundAndRebookShortRules.length > 0 && <ShortRules rules={refundAndRebookShortRules} />}

        {accrualPoints && <AccrualPoints cabin={cabin} accrualPoints={accrualPoints} />}
      </div>
    </div>
  );
}

type AccrualPointsProps = {
  cabin: Cabin | Cabin;
  accrualPoints: AccrualPointsData;
};

function AccrualPoints({ cabin, accrualPoints }: AccrualPointsProps) {
  let productAccrualPoints = '';

  if (accrualPoints?.data) {
    const productRewardPoints = accrualPoints.data?.find(
      reward => reward.productName?.toUpperCase() === cabin.productName.toUpperCase()
    );
    if (productRewardPoints && productRewardPoints.tierDetails) {
      const tierDetail = productRewardPoints.tierDetails.find(tier => tier.tierLevel === 'MEMBER');
      if (tierDetail && tierDetail.points) {
        productAccrualPoints = tierDetail.points;
      }
    }
  }

  return accrualPoints.isLoading ? (
    <Skeleton style={{ width: 60, display: 'inline-block' }} />
  ) : (
    productAccrualPoints && productAccrualPoints !== 'Not Available' && (
      <p css={styles.points}>
        <Trans i18nKey="earnPoints">
          Earn:&nbsp;<b>{{ points: productAccrualPoints }}</b>&nbsp;p
        </Trans>
      </p>
    )
  );
}
